import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useMemo,
    useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
    Phone,
    Email,
    Person,
    Home,
    Cake,
    Work,
    Transgender,
    LocationOn,
    Language,
    Fingerprint,
    Search,
    AdminPanelSettings,
    Business,
    Groups,
} from '@mui/icons-material';
import { FormControlLabel, Switch, TextField, Paper, Box, Chip, Checkbox } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon, Close } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';

import CustomInput from 'components/CustomInput/CustomInput.js';
import SparaButton from 'components/SparaButton/SparaButton';

import styles from './Step1.module.scss';

const ROLE_OPTIONS = [
    {
        id: 2,
        name: 'super_admin',
        label: 'Super Admin',
        description: 'Fullur aðgangur að öllu',
        icon: AdminPanelSettings,
        color: '#b71c1c',
        colorDark: '#ef9a9a',
        bg: '#ffebee',
    },
    {
        id: 4,
        name: 'company_user',
        label: 'Fyrirtækjastjórnandi',
        description: 'Aðgangur að völdum fyrirtækjum',
        icon: Business,
        color: '#0d47a1',
        colorDark: '#90caf9',
        bg: '#e3f2fd',
    },
    {
        id: 28,
        name: 'group_admin',
        label: 'Stjórnandi hópa',
        description: 'Aðgangur að völdum hópum',
        icon: Groups,
        color: '#1b5e20',
        colorDark: '#a5d6a7',
        bg: '#e8f5e9',
    },
];

const verifyEmail = (value) => {
    const emailRex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRex.test(value);
};

const findPostalCode = (data, id, type) => {
    const temp = data.find((x) => x.id === id);
    return temp ? temp[type] : '';
};

const extractInitialState = (props) => {
    const prop = props.user.torgid_user
        ? props.user.torgid_user
        : props.user.length > 1
        ? props.user[0].torgid_user
        : props.user;

    const userData = props.user.torgid_user || props.user;

    let roleIds = prop.roles ? prop.roles.map((r) => r.id) : [];
    let companyIds = props.user.companyIds
        ? props.user.companyIds.filter((item) => item !== null && item !== undefined)
        : [];
    let groupIds = props.user.groupIds
        ? props.user.groupIds.filter((item) => item !== null && item !== undefined)
        : [];
    let openForBarter = props.user.id ? props.user.open_for_barter : false;
    let hidden = props.user.id ? props.user.hidden : false;

    if (userData.roles && Array.isArray(userData.roles)) {
        roleIds = userData.roles.map((role) => role.id);
    }
    if (userData.torgid_company_users && Array.isArray(userData.torgid_company_users)) {
        companyIds = userData.torgid_company_users
            .map((cu) => cu.torgid_company_id)
            .filter((id) => id !== null && id !== undefined);
        const firstCU = userData.torgid_company_users[0];
        if (firstCU) {
            openForBarter = firstCU.open_for_barter || false;
            hidden = firstCU.hidden || false;
        }
    }

    return {
        id: prop.id || '',
        user: prop.email || prop.phone || '',
        userState: prop.email || prop.phone ? 'success' : '',
        email: prop.email || '',
        emailState: prop.email ? 'success' : '',
        password: '',
        passwordState: prop.id ? 'success' : '',
        name: prop.name || '',
        phone: prop.phone || '',
        phoneState: prop.phone ? 'success' : '',
        date_of_birth: prop.date_of_birth || null,
        gender: prop.gender || 'male',
        address: prop.address || '',
        torgid_region_id: prop.torgid_region_id || '',
        postalcode_number:
            prop.torgid_region_id && props.data?.results
                ? findPostalCode(props.data.results, prop.torgid_region_id, 'postal_code')
                : '',
        postalcode_city:
            prop.torgid_region_id && props.data?.results
                ? findPostalCode(props.data.results, prop.torgid_region_id, 'city')
                : '',
        subscribed: prop.id ? prop.subscribed : true,
        language: prop.language || 'IS',
        kennitala: prop.kennitala || '',
        kennitalaState: prop.kennitala ? 'success' : '',
        disabled: !prop.id,
        type: 'Company_User',
        login: 'company_user',
        profile_picture: prop.profile_picture || '',
        companyIds,
        role_ids: roleIds,
        groupState: prop.roles ? 'success' : '',
        groupIds,
        position: prop.position || '',
        companyState: props.user.companyIds ? 'success' : '',
        open_for_barter: openForBarter,
        hidden,
    };
};

const InfoField = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
        <Box>
            <div className={styles.infoLabel}>
                <Icon />
                {label}
            </div>
            <div className={styles.infoValue}>{value}</div>
        </Box>
    );
};

const Step1 = forwardRef(({ classes, user, companies, checkEmail, data, company }, ref) => {
    const { t } = useTranslation();
    const [state, setState] = useState(() =>
        extractInitialState({ user, companies, data, company })
    );

    const updateField = useCallback((field, value) => {
        setState((prev) => ({ ...prev, [field]: value }));
    }, []);

    const updateFields = useCallback((fields) => {
        setState((prev) => ({ ...prev, ...fields }));
    }, []);

    useEffect(() => {
        if (user.id && user.id !== state.id && !user.torgid_user) {
            const regions = data?.results || [];
            const regionId = user.torgid_region_id;
            const regionEntry = regionId > 0 ? regions.find((x) => x.id === regionId) : null;

            const updates = {
                id: user.id,
                email: user.email,
                name: user.name,
                phone: user.phone,
                date_of_birth: user.date_of_birth,
                gender: user.gender,
                address: user.address,
                torgid_region_id: regionId || '',
                postalcode_number: regionEntry ? regionEntry.postal_code : '',
                postalcode_city: regionEntry ? regionEntry.city : '',
                subscribed: user.id ? user.subscribed : true,
                language: user.language || 'IS',
                userState: user.email || user.phone ? 'success' : '',
                profile_picture: user.profile_picture || '',
                passwordState: user.id ? 'success' : '',
                emailState: user.email ? 'success' : '',
                phoneState: user.phone ? 'success' : '',
                position: user.position || '',
            };

            const userData = user.torgid_user || user;
            if (userData.roles && Array.isArray(userData.roles)) {
                updates.role_ids = userData.roles.map((role) => role.id);
                updates.groupState = userData.roles.length > 0 ? 'success' : '';
            } else {
                updates.role_ids = [];
                updates.groupState = '';
            }

            if (userData.torgid_company_users && Array.isArray(userData.torgid_company_users)) {
                updates.companyIds = userData.torgid_company_users
                    .map((cu) => cu.torgid_company_id)
                    .filter((id) => id !== null && id !== undefined);
                updates.companyState = updates.companyIds.length > 0 ? 'success' : '';
                const firstCU = userData.torgid_company_users[0];
                updates.open_for_barter = firstCU ? firstCU.open_for_barter : false;
                updates.hidden = firstCU ? firstCU.hidden : false;
            } else {
                updates.companyIds = [];
                updates.companyState = '';
                updates.open_for_barter = false;
                updates.hidden = false;
            }

            updateFields(updates);
        }
    }, [user.id]); // eslint-disable-line

    useImperativeHandle(ref, () => ({
        sendState: () => {
            const hasCompanies = state.companyIds.length > 0;
            const hasGroups = state.groupIds.length > 0;
            const sanitizedRoleIds = state.role_ids.filter((id) => {
                if (id === 4) return hasCompanies;
                if (id === 28) return hasGroups;
                return true;
            });
            return { ...state, role_ids: sanitizedRoleIds };
        },
    }));

    const handleCheckEmail = useCallback(() => {
        checkEmail(state.user);
    }, [checkEmail, state.user]);

    const handleInputChange = useCallback(
        (event, fieldName, validationType, minLength) => {
            const value = event.target.value;
            let validState = '';

            switch (validationType) {
                case 'login':
                    validState = verifyEmail(value) || /^\d{7}$/.test(value) ? 'success' : 'error';
                    break;
                case 'email':
                    validState = verifyEmail(value) ? 'success' : 'error';
                    break;
                case 'length':
                    validState = value.length >= minLength ? 'success' : 'error';
                    break;
                case 'phone':
                    validState = /^\d{7}$/.test(value) ? 'success' : 'error';
                    break;
                default:
                    break;
            }

            updateFields({ [fieldName]: value, [fieldName + 'State']: validState });
        },
        [updateFields]
    );

    const handleToggleRole = useCallback((roleId) => {
        const SUPER_ADMIN_ID = 2;
        const OTHER_IDS = [4, 28];

        setState((prev) => {
            const currentIds = prev.role_ids || [];

            if (currentIds.includes(roleId)) {
                return { ...prev, role_ids: currentIds.filter((id) => id !== roleId) };
            }

            if (roleId === SUPER_ADMIN_ID) {
                return { ...prev, role_ids: [SUPER_ADMIN_ID] };
            }

            return {
                ...prev,
                role_ids: [...currentIds.filter((id) => id !== SUPER_ADMIN_ID), roleId],
            };
        });
    }, []);

    const companyOptions = useMemo(() => {
        if (!companies?.results) return [];
        return companies.results.map((option) => {
            const firstLetter = option.name.charAt(0).toUpperCase();
            return {
                firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                ...option,
            };
        });
    }, [companies]);

    const groupOptions = useMemo(() => {
        if (!company?.results) return [];
        return company.results.map((option) => {
            const firstLetter = option.name.charAt(0).toUpperCase();
            return {
                firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                ...option,
            };
        });
    }, [company]);

    const validCompanyIds = useMemo(() => {
        if (!companyOptions.length) return state.companyIds;
        return state.companyIds.filter((id) => companyOptions.some((o) => o.id === id));
    }, [state.companyIds, companyOptions]);

    const validGroupIds = useMemo(() => {
        if (!groupOptions.length) return state.groupIds;
        return state.groupIds.filter((id) => groupOptions.some((o) => o.id === id));
    }, [state.groupIds, groupOptions]);

    useEffect(() => {
        if (validCompanyIds.length !== state.companyIds.length) {
            updateField('companyIds', validCompanyIds);
        }
    }, [validCompanyIds]); // eslint-disable-line

    useEffect(() => {
        if (validGroupIds.length !== state.groupIds.length) {
            updateField('groupIds', validGroupIds);
        }
    }, [validGroupIds]); // eslint-disable-line

    // Auto-deselect group_admin role (28) when all groups are removed
    useEffect(() => {
        if (state.groupIds.length === 0 && state.role_ids.includes(28)) {
            setState((prev) => ({
                ...prev,
                role_ids: prev.role_ids.filter((id) => id !== 28),
            }));
        }
    }, [state.groupIds.length]); // eslint-disable-line

    const showCompanySelector = state.role_ids.includes(3) || state.role_ids.includes(4);
    const showGroupSelector = state.role_ids.includes(28);
    const hasUserData = state.name || state.email || state.phone;

    return (
        <div className={styles.container}>
            {/* Search */}
            <div className={styles.searchSection}>
                <div className={styles.searchInput}>
                    <CustomInput
                        success={state.userState === 'success'}
                        error={state.userState === 'error'}
                        labelText={
                            t('company_user.search_error') ? 'Leita að notanda' : 'Leita að notanda'
                        }
                        id="login"
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                            value: state.user,
                            onChange: (event) => handleInputChange(event, 'user', 'login'),
                            placeholder: 'Sláðu inn tölvupóst eða símanúmer',
                            onKeyPress: (event) => {
                                if (event.key === 'Enter') handleCheckEmail();
                            },
                        }}
                    />
                </div>
                <SparaButton onClick={handleCheckEmail} color="primary" icon={Search} fullWidth>
                    {t('search')}
                </SparaButton>
            </div>

            {hasUserData && (
                <>
                    {/* User info */}
                    <Paper elevation={0} className={styles.infoCard}>
                        <div className={styles.infoGrid}>
                            <InfoField icon={Person} label={t('full_name')} value={state.name} />
                            <InfoField icon={Email} label={t('email')} value={state.email} />
                            <InfoField icon={Phone} label={t('phone_number')} value={state.phone} />
                            <InfoField icon={Home} label={t('address')} value={state.address} />
                            <InfoField
                                icon={Cake}
                                label={t('date_of_birth')}
                                value={state.date_of_birth}
                            />
                            <InfoField icon={Work} label={t('position')} value={state.position} />
                            <InfoField
                                icon={Transgender}
                                label={t('gender')}
                                value={
                                    state.gender
                                        ? state.gender === 'male'
                                            ? t('male')
                                            : t('female')
                                        : null
                                }
                            />
                            <InfoField
                                icon={LocationOn}
                                label={t('select_region')}
                                value={
                                    state.postalcode_number || state.postalcode_city
                                        ? `${state.postalcode_number} ${state.postalcode_city}`.trim()
                                        : null
                                }
                            />
                            <InfoField
                                icon={Language}
                                label={t('choose_language')}
                                value={
                                    state.language
                                        ? state.language === 'IS'
                                            ? t('icelandic')
                                            : t('english')
                                        : null
                                }
                            />
                            <InfoField
                                icon={Fingerprint}
                                label={t('kennitala')}
                                value={state.kennitala}
                            />
                        </div>
                    </Paper>

                    {/* Company + Group side by side — always visible */}
                    <div className={styles.sectionTitle}>{t('roles')}</div>
                    <div className={styles.rolePairContainer}>
                        {/* Company column */}
                        <div className={styles.rolePairColumn}>
                            {(() => {
                                const companyRole = ROLE_OPTIONS.find((r) => r.id === 4);
                                const isActive = state.role_ids.includes(4);
                                const CIcon = companyRole.icon;
                                return (
                                    <div
                                        className={`${styles.roleCard} ${
                                            isActive ? styles.active : ''
                                        }`}
                                        style={{
                                            '--role-color': companyRole.color,
                                            '--role-color-dark': companyRole.colorDark,
                                            '--role-bg': companyRole.bg,
                                        }}
                                        onClick={() => handleToggleRole(4)}
                                    >
                                        <div className={styles.roleIconBubble}>
                                            <CIcon style={{ fontSize: '22px' }} />
                                        </div>
                                        <div className={styles.roleCardText}>
                                            <p className={styles.roleCardName}>
                                                {companyRole.label}
                                            </p>
                                            <p className={styles.roleCardDesc}>
                                                {companyRole.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div
                                className={`${styles.selectorSection} ${
                                    showCompanySelector
                                        ? styles.selectorOpen
                                        : styles.selectorClosed
                                }`}
                            >
                                <div className={styles.selectorInner}>
                                    {validCompanyIds.length > 0 && (
                                        <div className={styles.selectedChips}>
                                            {validCompanyIds
                                                .map((id) =>
                                                    companyOptions.find((o) => o.id === id)
                                                )
                                                .filter(Boolean)
                                                .map((c) => (
                                                    <div
                                                        key={c.id}
                                                        className={styles.selectedChip}
                                                        data-variant="company"
                                                    >
                                                        {c.logo && (
                                                            <img
                                                                src={c.logo}
                                                                alt=""
                                                                className={styles.chipLogo}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        )}
                                                        <span className={styles.chipName}>
                                                            {c.name}
                                                        </span>
                                                        <Close
                                                            className={styles.chipRemove}
                                                            onClick={() =>
                                                                updateFields({
                                                                    companyIds: validCompanyIds.filter(
                                                                        (cid) => cid !== c.id
                                                                    ),
                                                                    companyState: 'success',
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                    <Autocomplete
                                        multiple
                                        id="companies"
                                        onChange={(_, newValue) => {
                                            updateFields({
                                                companyIds: newValue.map((v) => v.id),
                                                companyState: 'success',
                                            });
                                        }}
                                        value={validCompanyIds
                                            .map((id) => companyOptions.find((o) => o.id === id))
                                            .filter(Boolean)}
                                        options={companyOptions
                                            .slice()
                                            .sort(
                                                (a, b) =>
                                                    -b.firstLetter.localeCompare(a.firstLetter)
                                            )}
                                        groupBy={(option) => option.firstLetter}
                                        getOptionLabel={(option) => option?.name || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        renderTags={() => null}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props} key={option.id}>
                                                <Checkbox
                                                    icon={
                                                        <CheckBoxOutlineBlank
                                                            style={{ fontSize: 18 }}
                                                        />
                                                    }
                                                    checkedIcon={
                                                        <CheckBoxIcon style={{ fontSize: 18 }} />
                                                    }
                                                    style={{ marginRight: 8, padding: 2 }}
                                                    checked={selected}
                                                />
                                                {option.logo && (
                                                    <img
                                                        src={option.logo}
                                                        alt=""
                                                        style={{
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            marginRight: 8,
                                                            border: '1px solid #e2e8f0',
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <span style={{ fontSize: '0.875rem' }}>
                                                    {option.name}
                                                </span>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Veldu fyrirtæki..."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        backgroundColor: '#fff',
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Group column */}
                        <div className={styles.rolePairColumn}>
                            {(() => {
                                const groupRole = ROLE_OPTIONS.find((r) => r.id === 28);
                                const isActive = state.role_ids.includes(28);
                                const GIcon = groupRole.icon;
                                return (
                                    <div
                                        className={`${styles.roleCard} ${
                                            isActive ? styles.active : ''
                                        }`}
                                        style={{
                                            '--role-color': groupRole.color,
                                            '--role-color-dark': groupRole.colorDark,
                                            '--role-bg': groupRole.bg,
                                        }}
                                        onClick={() => handleToggleRole(28)}
                                    >
                                        <div className={styles.roleIconBubble}>
                                            <GIcon style={{ fontSize: '22px' }} />
                                        </div>
                                        <div className={styles.roleCardText}>
                                            <p className={styles.roleCardName}>{groupRole.label}</p>
                                            <p className={styles.roleCardDesc}>
                                                {groupRole.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div
                                className={`${styles.selectorSection} ${
                                    showGroupSelector ? styles.selectorOpen : styles.selectorClosed
                                }`}
                            >
                                <div className={styles.selectorInner}>
                                    {validGroupIds.length > 0 && (
                                        <div className={styles.selectedChips}>
                                            {validGroupIds
                                                .map((id) => groupOptions.find((o) => o.id === id))
                                                .filter(Boolean)
                                                .map((g) => (
                                                    <div
                                                        key={g.id}
                                                        className={styles.selectedChip}
                                                        data-variant="group"
                                                    >
                                                        {g.logo && (
                                                            <img
                                                                src={g.logo}
                                                                alt=""
                                                                className={styles.chipLogo}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        )}
                                                        <span className={styles.chipName}>
                                                            {g.name}
                                                        </span>
                                                        <Close
                                                            className={styles.chipRemove}
                                                            onClick={() =>
                                                                updateFields({
                                                                    groupIds: validGroupIds.filter(
                                                                        (gid) => gid !== g.id
                                                                    ),
                                                                    groupState: 'success',
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                    <Autocomplete
                                        multiple
                                        id="groups"
                                        onChange={(_, newValue) => {
                                            updateFields({
                                                groupIds: newValue.map((v) => v.id),
                                                groupState: 'success',
                                            });
                                        }}
                                        value={validGroupIds
                                            .map((id) => groupOptions.find((o) => o.id === id))
                                            .filter(Boolean)}
                                        options={groupOptions
                                            .slice()
                                            .sort(
                                                (a, b) =>
                                                    -b.firstLetter.localeCompare(a.firstLetter)
                                            )}
                                        getOptionLabel={(option) => option?.name || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        renderTags={() => null}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props} key={option.id}>
                                                <Checkbox
                                                    icon={
                                                        <CheckBoxOutlineBlank
                                                            style={{ fontSize: 18 }}
                                                        />
                                                    }
                                                    checkedIcon={
                                                        <CheckBoxIcon style={{ fontSize: 18 }} />
                                                    }
                                                    style={{ marginRight: 8, padding: 2 }}
                                                    checked={selected}
                                                />
                                                {option.logo && (
                                                    <img
                                                        src={option.logo}
                                                        alt=""
                                                        style={{
                                                            width: 22,
                                                            height: 22,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            marginRight: 8,
                                                            border: '1px solid #e2e8f0',
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <span style={{ fontSize: '0.875rem' }}>
                                                    {option.name}
                                                </span>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Veldu hóp..."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        backgroundColor: '#fff',
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Super Admin - below, always visible */}
                    {(() => {
                        const superAdmin = ROLE_OPTIONS.find((r) => r.id === 2);
                        const isSuperActive = state.role_ids.includes(2);
                        const SAIcon = superAdmin.icon;
                        return (
                            <div
                                className={`${styles.roleCard} ${
                                    isSuperActive ? styles.active : ''
                                }`}
                                style={{
                                    '--role-color': superAdmin.color,
                                    '--role-color-dark': superAdmin.colorDark,
                                    '--role-bg': superAdmin.bg,
                                    marginBottom: '24px',
                                }}
                                onClick={() => handleToggleRole(2)}
                            >
                                <div className={styles.roleIconBubble}>
                                    <SAIcon style={{ fontSize: '22px' }} />
                                </div>
                                <div className={styles.roleCardText}>
                                    <p className={styles.roleCardName}>{superAdmin.label}</p>
                                    <p className={styles.roleCardDesc}>{superAdmin.description}</p>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Switches */}
                    <div className={styles.switchesContainer}>
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={state.disabled}
                                    checked={state.open_for_barter || false}
                                    onChange={(e) =>
                                        updateField('open_for_barter', e.target.checked)
                                    }
                                    classes={{
                                        switchBase: classes?.switchBase,
                                        checked: classes?.switchChecked,
                                        thumb: classes?.switchIcon,
                                        track: classes?.switchBar,
                                    }}
                                />
                            }
                            classes={{ label: styles.switchLabel }}
                            label={t('company_user.open_to_exchange')}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={state.disabled}
                                    checked={state.hidden || false}
                                    onChange={(e) => updateField('hidden', e.target.checked)}
                                    classes={{
                                        switchBase: classes?.switchBase,
                                        checked: classes?.switchChecked,
                                        thumb: classes?.switchIcon,
                                        track: classes?.switchBar,
                                    }}
                                />
                            }
                            classes={{ label: styles.switchLabel }}
                            label={t('company_user.hidden')}
                        />
                    </div>
                </>
            )}
        </div>
    );
});

Step1.displayName = 'Step1';

export default Step1;
